import React, { useCallback, useEffect, useState, useMemo, useRef, useLayoutEffect } from "react";
import { getApi } from "../../../Services/apiService";
import { getFamilyTreeIdFromStorage } from "../../../Utils/headOfLineRole";
import {
  isDashboardStartOrTopOfLineMember,
  isExplicitDashboardStartOrTopOfLineMember,
} from "../../../Utils/dashboardStartOfLineMember";
import {
  getMultipleBirthSuperscriptText,
  getMultipleBirthLegendTags,
} from "../../../Utils/dashboardMultipleBirthSuperscript";
import {
  formatDashboardMemberDisplayName,
  formatDashboardStartTopLineFullName,
  getDashboardMemberNameParts,
  getDashboardInRelationshipMemberNameParts,
  getDashboardMemberLineColor,
  isDashboardDeceasedDescendantMember,
  isDashboardDeceasedInLawMember,
  isDashboardDescendantMember,
  isDashboardInLawMember,
  isDashboardInRelationshipInLawMember,
  isDashboardLivingInLawMember,
} from "../../../Utils/dashboardMemberDisplayName";
import {
  DashboardPhotoHeadLine,
  DashboardPhotoModal,
  DashboardPhotoName,
  DashboardMemberNameLabel,
  DashboardInLawAsterisk,
  DashboardNameMarkerGroup,
  getDashboardInRelationshipPhotoUrls,
  getDashboardMemberNamePhotoUrls,
  getDashboardMemberPhotoUrl,
  useDashboardPhotoModal,
} from "./DashboardPhotoViewer";
import DashboardLineTotalsPopup, {
  buildDashboardLineTotals,
} from "./DashboardLineTotalsPopup";
import { getMemberDeathYear } from "../../../Utils/family-tree/dateAndTime";
import {
  buildDashboardFamilyLineNumberMap,
  compareDashboardMembersByYoungest,
  getDashboardFamilyLineNumber,
  sortDashboardMembersByAgeWithInLawsAlphabetical,
  sortDashboardMembersByOldestFamilyOrder,
  sortDashboardMembersOldestToYoungest,
  sortDashboardMembersByDeathDate,
  applyDashboardInLawsLastOrder,
  sortDashboardInLawsAlphabetically,
  sortDashboardMembersByFamilyLineNumberOrder,
} from "../../../Utils/dashboardFamilyLineNumbers";
import {
  buildDashboardColorLegendLines,
  buildHeadsOfLineIdByColorMap,
  getDashboardFamilyLineHeadDisplayName,
  extractAllMembersFromStatisticsResponse,
  extractHeadsOfLineFromStatisticsResponse,
} from "../../../Utils/dashboardFamilyLineNavigation";
import DashboardBackButton from "./DashboardBackButton";
import DashboardColorLegend from "./DashboardColorLegend";
import DashboardDetailSearchBox, {
  DashboardDetailSearchNoMatch,
} from "./DashboardDetailSearchBox";
import DashboardDetailSortFilterBar from "./DashboardDetailSortFilterBar";
import DashboardMarkerLegend from "./DashboardMarkerLegend";
import DashboardLineColorName from "./DashboardLineColorName";
import DashboardPhotoTapHint from "./DashboardPhotoTapHint";
import {
  applyDashboardMemberFilterToggle,
  filterDashboardMembersByMemberFilter,
} from "../../../Utils/dashboardDetailMemberFilter";
import { filterDashboardMembersBySearch } from "../../../Utils/dashboardDetailSearch";

function TotalFamilyMembers() {
  const { photos: photoModalPhotos, openPhoto, closePhoto } = useDashboardPhotoModal();
  const [headsMap, setHeadsMap] = useState(() => buildHeadsOfLineIdByColorMap([]));
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isLargeView, setIsLargeView] = useState(false);
  const contentWrapperRef = useRef(null);
  const listClusterRef = useRef(null);
  const columnsRef = useRef(null);
  const measureListRef = useRef(null);
  const firstColumnRef = useRef(null);
  const [columnSplitIndex, setColumnSplitIndex] = useState(0);
  const [nameColumnWidth, setNameColumnWidth] = useState(null);
  const [isCompactLayout, setIsCompactLayout] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 768
  );

  useEffect(() => {
    const updateLayoutMode = () => setIsCompactLayout(window.innerWidth <= 768);

    updateLayoutMode();
    window.addEventListener("resize", updateLayoutMode);

    return () => window.removeEventListener("resize", updateLayoutMode);
  }, []);

  const getMeasuredRowHeight = (wrappers, index) => {
    const wrapper = wrappers[index];
    const row = wrapper?.querySelector(".dashboard-detail-row");
    const marginBottom = row
      ? parseFloat(window.getComputedStyle(row).marginBottom) || 0
      : 0;

    if (index + 1 < wrappers.length) {
      const nextTop = wrappers[index + 1].offsetTop;
      const currentTop = wrapper.offsetTop;
      const delta = nextTop - currentTop;
      if (delta > 0) return delta;
    }

    if (row) {
      const rowHeight = row.getBoundingClientRect().height;
      if (rowHeight > 0) return rowHeight + marginBottom;
    }

    const wrapperHeight = wrapper.offsetHeight;
    if (wrapperHeight > 0) return wrapperHeight + marginBottom;

    return 20;
  };

  // Fetch all family members from API
  useEffect(() => {
    const fetchAllMembers = async () => {
      try {
        setLoading(true);
        const family_tree_id = getFamilyTreeIdFromStorage();

        if (!family_tree_id) {
          setMembers([]);
          setLoading(false);
          return;
        }

        const response = await getApi(`/dashboard-statistics-with-details?family_tree_id=${family_tree_id}`);
        
        if (response?.statusCode === 200 && response?.data) {
          setHeadsMap(
            buildHeadsOfLineIdByColorMap(
              extractHeadsOfLineFromStatisticsResponse(response.data),
              extractAllMembersFromStatisticsResponse(response.data)
            )
          );
          // Extract family members from the response
          // The API returns: data.total_family_members.details (array)
          const membersData = 
            response?.data?.total_family_members?.details || 
            response?.data?.total_family_members ||
            [];
          
          // Ensure it's always an array
          if (Array.isArray(membersData)) {
            setMembers(membersData);
          } else {
            console.warn("Family members data is not an array:", membersData);
            setMembers([]);
          }
        } else {
          setMembers([]);
        }
      } catch (error) {
        console.error("Error fetching family members:", error);
        setMembers([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAllMembers();
  }, []);

  const [sortBy, setSortBy] = useState("none"); // "none", "alpha-asc", "alpha-desc", "youngest", "oldest", "color", "death-asc"
  // Picking the Line Color sort opens the per-line totals popup.
  const [showLineTotals, setShowLineTotals] = useState(false);
  const handleSortChange = (next) => {
    setSortBy(next);
    setShowLineTotals(next === "color");
  };
  const closeLineTotals = useCallback(() => setShowLineTotals(false), []);
  // "all" | "in-laws" | "in-laws-deceased" | "in-relationship" | "related" |
  // "related-living" | "related-deceased" | "start-top" — this page shows the
  // Related tabs (blood family plus the Start/Top of Line couple) where the other
  // detail pages show Descendants.
  const [memberFilter, setMemberFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const deathDateNameOptions = { appendDescendantSpouse: false };

  // The two sides of the bar are one choice apiece and never read as selected
  // together. A member tab on the right picks who is listed and clears the sort
  // side, so the tab stands selected on its own; the list still lands in the
  // order that tab is read in — the sortBy === "none" branch below, which is
  // what each tab shows on its own. Clicking a sort afterwards lights it on the
  // left and orders the tab's list by it.
  //
  // The sort reset runs here in the click handler, not inside a setState
  // updater: updaters run in the render phase, where React does not reliably
  // apply a setState for another piece of state — the list sorted but the sort
  // bar never lit the matching button up.
  const toggleMemberFilter = (filter) => {
    setMemberFilter(
      applyDashboardMemberFilterToggle(memberFilter, filter, setSortBy)
    );
  };

  const lineNumberMap = useMemo(
    () => buildDashboardFamilyLineNumberMap(members),
    [members]
  );

  const formatPersonName = (member) => {
    if (
      memberFilter === "start-top" &&
      isDashboardStartOrTopOfLineMember(member)
    ) {
      return formatDashboardStartTopLineFullName(member);
    }
    return formatDashboardMemberDisplayName(member, { appendDescendantSpouse: false });
  };

  // The member tab narrows the list first, then the search box narrows it
  // again, so the count and the sort both read the rows actually on screen.
  const filteredMembers = useMemo(
    () =>
      filterDashboardMembersBySearch(
        filterDashboardMembersByMemberFilter(members, memberFilter),
        searchQuery,
        formatPersonName
      ),
    [members, memberFilter, searchQuery]
  );

  // Sort members based on sortBy (after in-law / descendants filter)
  const sortedMembers = useMemo(() => {
    if (!filteredMembers.length) return filteredMembers;
    if (sortBy === "none") {
      if (
        memberFilter === "related-deceased" ||
        memberFilter === "in-laws-deceased"
      ) {
        return sortDashboardMembersByDeathDate(
          filteredMembers,
          deathDateNameOptions
        );
      }
      if (
        memberFilter === "related" ||
        memberFilter === "related-living" ||
        memberFilter === "start-top"
      ) {
        return sortDashboardMembersByOldestFamilyOrder(
          filteredMembers,
          memberFilter
        );
      }
      if (
        memberFilter === "in-laws" ||
        memberFilter === "in-relationship"
      ) {
        // Living in-laws and In Relationship read as plain name lists, so on
        // their own they land in A–Z.
        return sortDashboardInLawsAlphabetically(filteredMembers, {
          appendDescendantSpouse: false,
        });
      }
      if (memberFilter === "all") {
        return sortDashboardMembersByFamilyLineNumberOrder(
          filteredMembers,
          memberFilter,
          // Same name options the rows render with, so the A–Z run inside each
          // in-law colour group matches the names actually on screen.
          { lineNumberMap, nameOptions: { appendDescendantSpouse: false } }
        );
      }
      return applyDashboardInLawsLastOrder(filteredMembers, memberFilter);
    }

    if (sortBy === "death-asc") {
      return sortDashboardMembersByDeathDate(
        filteredMembers,
        deathDateNameOptions
      );
    }

    // A sort button applies to the whole visible list — in-laws and Start/Top
    // are sorted in with everyone else, not parked at the end. With a member
    // filter active the visible list is already that one group, so the same
    // whole-list sort is what scopes it to the group.
    //
    // The two age sorts are the exception: in-laws are not counted in anything
    // involving age, so they are left out of the ranking and trail it A–Z.
    if (sortBy === "oldest") {
      return sortDashboardMembersByAgeWithInLawsAlphabetical(
        filteredMembers,
        sortDashboardMembersOldestToYoungest,
        { nameOptions: { appendDescendantSpouse: false } }
      );
    }

    if (sortBy === "alpha-asc") {
      return [...filteredMembers].sort((a, b) => {
        const nameA = formatPersonName(a).toLowerCase();
        const nameB = formatPersonName(b).toLowerCase();
        return nameA.localeCompare(nameB);
      });
    }

    if (sortBy === "alpha-desc") {
      return [...filteredMembers].sort((a, b) => {
        const nameA = formatPersonName(a).toLowerCase();
        const nameB = formatPersonName(b).toLowerCase();
        return nameB.localeCompare(nameA);
      });
    }

    if (sortBy === "youngest") {
      return sortDashboardMembersByAgeWithInLawsAlphabetical(
        filteredMembers,
        (list) => [...list].sort(compareDashboardMembersByYoungest),
        { nameOptions: { appendDescendantSpouse: false } }
      );
    }

    if (sortBy === "color") {
      // Include in-laws in the same line/color groups (do not park them at the end).
      return [...filteredMembers].sort((a, b) => {
        const colorA = getDashboardMemberLineColor(a, members).colorName.toLowerCase();
        const colorB = getDashboardMemberLineColor(b, members).colorName.toLowerCase();
        if (colorA === colorB) {
          const nameA = formatPersonName(a).toLowerCase();
          const nameB = formatPersonName(b).toLowerCase();
          return nameA.localeCompare(nameB);
        }
        return colorA.localeCompare(colorB);
      });
    }

    return applyDashboardInLawsLastOrder(filteredMembers, memberFilter);
  }, [filteredMembers, sortBy, memberFilter, lineNumberMap, members]);

  const showLineNumberColumn = useMemo(
    () =>
      sortedMembers.some(
        (member) => getDashboardFamilyLineNumber(member, lineNumberMap) != null
      ),
    [sortedMembers, lineNumberMap]
  );

  const showDeathYearColumn =
    memberFilter === "related-deceased" ||
    memberFilter === "in-laws-deceased";

  // Keep name column width consistent across left/right list panels
  useLayoutEffect(() => {
    if (loading || sortedMembers.length === 0) {
      setNameColumnWidth(null);
      return;
    }

    const measureEl = measureListRef.current;
    if (!measureEl) return;

    // Measured to the end of each name's marker group (name, *, angel, twin
    // tag) rather than the cell's scrollWidth: the measure rows carry the width
    // set on the last pass, so scrollWidth could only ever grow and left the
    // line column far past the longest name.
    const measureNameColumnWidth = () => {
      const nameCols = measureEl.querySelectorAll(".dashboard-detail-name-col");
      let maxWidth = 0;
      nameCols.forEach((col) => {
        const group = col.querySelector(".dashboard-name-marker-group");
        if (!group) return;
        maxWidth = Math.max(
          maxWidth,
          group.getBoundingClientRect().right - col.getBoundingClientRect().left
        );
      });
      if (maxWidth > 0) {
        setNameColumnWidth(Math.ceil(maxWidth));
      }
    };

    measureNameColumnWidth();
    let cancelled = false;
    document.fonts?.ready?.then(() => {
      if (!cancelled) measureNameColumnWidth();
    });

    const ro = new ResizeObserver(measureNameColumnWidth);
    ro.observe(measureEl);
    window.addEventListener("resize", measureNameColumnWidth);

    return () => {
      cancelled = true;
      ro.disconnect();
      window.removeEventListener("resize", measureNameColumnWidth);
    };
  }, [sortedMembers, loading, isLargeView, memberFilter, sortBy, showLineNumberColumn, showDeathYearColumn]);

  const renderMemberRow = (member) => {
    let baseName;
    let descendantSpouseName;
    if (memberFilter === "in-relationship") {
      const parts = getDashboardInRelationshipMemberNameParts(member);
      baseName = parts.baseName;
      descendantSpouseName = parts.partnerName;
    } else {
      const parts = getDashboardMemberNameParts(member, {
        appendDescendantSpouse: false,
      });
      baseName = parts.baseName;
      descendantSpouseName = parts.descendantSpouseName;
    }
    const lineNumber = getDashboardFamilyLineNumber(member, lineNumberMap);
    const showInLawAsterisk =
      isDashboardInLawMember(member) &&
      !isExplicitDashboardStartOrTopOfLineMember(member);
    const isDeceased = member?.present_or_deceased === 2;
    const multipleBirthSup = getMultipleBirthSuperscriptText(member);
    const headOfLineName = getDashboardFamilyLineHeadDisplayName(member, headsMap);
    const lineColor = getDashboardMemberLineColor(member, members);
    const hexCode = lineColor.hexCode || "#000000";
    const colorName = lineColor.colorName;

    const iconSize = isLargeView ? "18px" : "12px";

    return (
      <div className={`dashboard-detail-row ${isLargeView ? "large-view" : ""}`}>
        <div
          className={`dashboard-detail-name-col ${isLargeView ? "large-view" : ""}${
            !showLineNumberColumn ? " dashboard-detail-name-col--no-line-number" : ""
          }`}
          style={
            // Compact is a single grid column sized to the longest name, so the
            // measured width — taken for the desktop two-panel split — would only
            // clip the names into the colour column beside them.
            nameColumnWidth && !showDeathYearColumn && !isCompactLayout
              ? {
                  width: `${nameColumnWidth}px`,
                  minWidth: `${nameColumnWidth}px`,
                }
              : undefined
          }
        >
          {showLineNumberColumn && (
            <span className="dashboard-family-line-number" aria-hidden={lineNumber == null}>
              {lineNumber ?? ""}
            </span>
          )}
          <DashboardNameMarkerGroup>
            <DashboardPhotoName
              {...(memberFilter === "in-relationship"
                ? getDashboardInRelationshipPhotoUrls(member)
                : getDashboardMemberNamePhotoUrls(member))}
              onViewPhoto={openPhoto}
            >
              <DashboardMemberNameLabel
                baseName={baseName}
                descendantSpouseName={descendantSpouseName}
                afterBaseName={
                  showInLawAsterisk && descendantSpouseName ? (
                    <DashboardInLawAsterisk className="total-family-members-page in-law-asterisk" isLargeView={isLargeView} />
                  ) : null
                }
              />
            </DashboardPhotoName>
            {showInLawAsterisk && !descendantSpouseName && (
              <DashboardInLawAsterisk className="total-family-members-page in-law-asterisk" isLargeView={isLargeView} />
            )}
            {isDeceased && (
              <span className={`dashboard-detail-angel-icon ${isLargeView ? "large-view" : ""}`}>
                <img
                  src={window.getPublicUrl("/assets/images/angel.png")}
                  alt="deceased"
                  style={{ width: iconSize, height: iconSize }}
                />
              </span>
            )}
            {multipleBirthSup && (
              <sup
                className={`dashboard-multiple-birth-sup${isLargeView ? " large-view" : ""}`}
              >
                {multipleBirthSup}
              </sup>
            )}
          </DashboardNameMarkerGroup>
        </div>

        {showDeathYearColumn && (
          <div
            className={`dashboard-detail-death-year-col ${isLargeView ? "large-view-text" : ""}`}
          >
            {getMemberDeathYear(member)}
          </div>
        )}

        {/* Compact swaps the head-of-line column for the line colour spelled out in
            its own colour; the footer legend ties each colour to its head. */}
        {isCompactLayout ? (
          <div className="dashboard-detail-line-col dashboard-detail-line-col--compact">
            <DashboardLineColorName colorName={colorName} hexCode={hexCode} />
          </div>
        ) : (
          <div
            className={`dashboard-detail-line-col ${isLargeView ? "large-view-text" : ""}`}
          >
            <DashboardPhotoHeadLine
              name={headOfLineName || "Start/Top of Line"}
              hexCode={hexCode}
              colorName={colorName ? `${colorName} Line` : ""}
              imageUrl={getDashboardMemberPhotoUrl(member, "head_image")}
              onViewPhoto={openPhoto}
              member={member}
              headsMap={headsMap}
            />
          </div>
        )}
      </div>
    );
  };

  const totalCount = filteredMembers.length;
  // Label for the active view — on compact layout this drops to its own line
  // under "Total Family Members", together with the count.
  const filterLabel =
    memberFilter === "in-laws"
      ? "In-Laws (Living)"
      : memberFilter === "in-laws-deceased"
        ? "In-Laws (Deceased)"
        : memberFilter === "in-relationship"
          ? "In Relationship"
          : memberFilter === "related"
          ? "Related"
          : memberFilter === "related-living"
          ? "Related (Living)"
          : memberFilter === "related-deceased"
            ? "Related (Deceased)"
            : memberFilter === "start-top"
              ? "Start/Top of Line"
              : "(Including In-Laws)";
  const isDefaultMemberFilter = filterLabel === "(Including In-Laws)";
  const listTitle = isDefaultMemberFilter ? "Family Members" : filterLabel;

  // Built from the whole tree rather than the filtered rows, so the footer legend
  // lists the same family lines here as on every other dashboard list, and does not
  // shed lines as the member filters narrow the page.
  const legendLines = useMemo(
    () => buildDashboardColorLegendLines(members, headsMap),
    [members, headsMap]
  );

  // Compact only: the family-lines legend is what ties each row's colour name back
  // to a head of line there. The desktop layout already names the head beside every
  // row, so the footer list only repeated what the rows say.
  const showColorLegend = isCompactLayout && legendLines.length > 0;

  // The Line Color popup's totals: the rows on screen, so the lines add up to
  // the count in the heading under any member tab or search. The deceased count
  // too, and an in-law with no line colour of their own counts on their spouse's.
  const lineTotals = useMemo(
    () =>
      buildDashboardLineTotals(
        filteredMembers.map((member) => {
          const { colorName, hexCode } = getDashboardMemberLineColor(member, members);
          return { colorName, hexCode: hexCode || "#000000" };
        }),
        legendLines
      ),
    [filteredMembers, legendLines, members]
  );

  // Split list into two columns: first fills the viewport, second scrolls
  useLayoutEffect(() => {
    if (loading || sortedMembers.length === 0) {
      setColumnSplitIndex(0);
      return;
    }

    // Never split into two columns on a phone — one scrolling column instead
    if (isCompactLayout) {
      setColumnSplitIndex(sortedMembers.length);
      return;
    }

    const columnsEl = columnsRef.current;
    const measureEl = measureListRef.current;
    if (!columnsEl || !measureEl) return;

    const calculateSplit = () => {
      const availableHeight = columnsEl.clientHeight;
      if (availableHeight <= 0) {
        setColumnSplitIndex(sortedMembers.length);
        return;
      }

      const rowWrappers = measureEl.querySelectorAll(":scope > div");
      if (rowWrappers.length === 0) {
        setColumnSplitIndex(sortedMembers.length);
        return;
      }

      let cumulative = 0;
      let splitAt = rowWrappers.length;

      for (let i = 0; i < rowWrappers.length; i++) {
        const rowHeight = getMeasuredRowHeight(rowWrappers, i);
        if (cumulative + rowHeight > availableHeight) {
          splitAt = Math.max(1, i);
          break;
        }
        cumulative += rowHeight;
      }

      while (splitAt > 1) {
        let totalHeight = 0;
        for (let i = 0; i < splitAt; i++) {
          totalHeight += getMeasuredRowHeight(rowWrappers, i);
        }
        if (totalHeight <= availableHeight) break;
        splitAt -= 1;
      }

      setColumnSplitIndex(splitAt);
    };

    calculateSplit();

    const ro = new ResizeObserver(calculateSplit);
    ro.observe(columnsEl);
    if (listClusterRef.current) ro.observe(listClusterRef.current);
    window.addEventListener("resize", calculateSplit);

    return () => {
      ro.disconnect();
      window.removeEventListener("resize", calculateSplit);
    };
  }, [
    sortedMembers,
    loading,
    isLargeView,
    memberFilter,
    sortBy,
    showDeathYearColumn,
    isCompactLayout,
  ]);

  const firstColumnMembers =
    columnSplitIndex > 0
      ? sortedMembers.slice(0, columnSplitIndex)
      : sortedMembers;
  const secondColumnMembers =
    columnSplitIndex > 0 && columnSplitIndex < sortedMembers.length
      ? sortedMembers.slice(columnSplitIndex)
      : [];
  const isSingleColumnList = secondColumnMembers.length === 0;

  // After render, trim first column if any row still overflows the visible area
  useLayoutEffect(() => {
    if (loading || columnSplitIndex <= 0 || columnSplitIndex >= sortedMembers.length) {
      return;
    }

    const columnsEl = columnsRef.current;
    const firstColEl = firstColumnRef.current;
    if (!columnsEl || !firstColEl) return;

    const columnsBottom = columnsEl.getBoundingClientRect().bottom;
    const lastRow = firstColEl.lastElementChild;
    if (!lastRow) return;

    const lastBottom = lastRow.getBoundingClientRect().bottom;
    if (lastBottom > columnsBottom + 0.5 && columnSplitIndex > 1) {
      setColumnSplitIndex((prev) => Math.max(1, prev - 1));
    }
  }, [columnSplitIndex, sortedMembers.length, loading, firstColumnMembers.length]);

  return (
    <div className={`dashboard-container total-family-members-page dashboard-detail-page ${isLargeView ? 'large-view-mode' : ''}`}>
      <div className="dash-box dashboard-detail-box">
        <div className="dashboard-detail-header">
          <div className="dashboard-detail-header-left">
          <DashboardBackButton />
            {isCompactLayout ? (
              <div className="dashboard-detail-heading-label-cluster dashboard-detail-heading-label-cluster--split">
                {/* Grid, not nesting: the count is a sibling of both title lines so
                    it can sit two spaces past the longer of the two, not past the
                    filter label it shares a row with. The default view has no
                    second line — "(Including In-Laws)" says nothing the plain
                    title does not, so on a phone the heading is just the title. */}
                <h3
                  className={`dashboard-detail-title dashboard-detail-title--split-grid${
                    isDefaultMemberFilter
                      ? " dashboard-detail-title--single-line"
                      : ""
                  }`}
                >
                  <span className="dashboard-detail-title-line">Family Members</span>
                  {!isDefaultMemberFilter && (
                    <span className="dashboard-detail-title-line dashboard-detail-title-secondary-line">
                      {filterLabel}
                    </span>
                  )}
                  <span className="dashboard-detail-count">{totalCount}</span>
                </h3>
              </div>
            ) : (
              <div className="dashboard-detail-heading-label-cluster">
                <h3 className="dashboard-detail-title">{listTitle}</h3>
                <div className="dashboard-detail-count">{totalCount}</div>
              </div>
            )}
            {!loading && members.length > 0 && (
              <DashboardDetailSearchBox
                value={searchQuery}
                onChange={setSearchQuery}
              />
            )}
            {!loading && members.length > 0 && (
              <DashboardDetailSortFilterBar
                className="dashboard-detail-sort-toggle total-family-members-sort-toggle"
                sortBy={sortBy}
                onSortChange={handleSortChange}
                memberFilter={memberFilter}
                onMemberFilterToggle={toggleMemberFilter}
                // Related, not Descendants: the Start/Top of Line couple is
                // counted with the blood family on this page.
                relatedInsteadOfDescendants
                // The page opens with no sort picked and the list already runs
                // oldest to youngest, so that button is grayed out until another
                // sort takes over.
                defaultSortByMemberFilter={{ all: "oldest" }}
              />
            )}
          </div>
        </div>

        {loading ? (
          <div className="dashboard-detail-empty-state">
            <p>Loading family members...</p>
          </div>
        ) : members.length === 0 ? (
          <div className="dashboard-detail-empty-state">
            <p>No data for this period.</p>
                </div>
        ) : filteredMembers.length === 0 ? (
          <div className="dashboard-detail-empty-state">
            {searchQuery.trim() ? (
              <DashboardDetailSearchNoMatch query={searchQuery} />
            ) : (
              <p>No data for this period.</p>
            )}
          </div>
        ) : (
          <div
            ref={contentWrapperRef}
            className="total-family-members-page dashboard-detail-content-wrapper"
          >
            <div
              ref={listClusterRef}
              className="total-family-members-list-cluster"
            >
              <div
                ref={measureListRef}
                className="family-members-list-measure"
                aria-hidden="true"
              >
                {sortedMembers.map((member, index) => (
                  <div key={`measure-${member?.id || index}`}>
                    {renderMemberRow(member)}
                  </div>
                ))}
              </div>
              <div ref={columnsRef} className="family-members-columns">
                <div
                  ref={firstColumnRef}
                  className={`family-members-list family-members-list-first${
                    showDeathYearColumn ? " family-members-list--with-death-year" : ""
                  }${isSingleColumnList ? " family-members-list--scrollable" : ""}`}
                >
                  {firstColumnMembers.map((member, index) => (
                    <div key={member?.id || index}>
                      {renderMemberRow(member)}
                    </div>
                  ))}
                </div>
                {secondColumnMembers.length > 0 && (
                  <>
                    <div className="total-family-members-page family-members-column-divider" />
                    <div
                      className={`family-members-list family-members-list-scroll${
                        showDeathYearColumn ? " family-members-list--with-death-year" : ""
                      }`}
                    >
                    {secondColumnMembers.map((member, index) => (
                      <div key={member?.id || index}>
                        {renderMemberRow(member)}
                      </div>
                    ))}
                  </div>
                  </>
                )}
              </div>
            </div>
            <div className="total-family-members-page in-law-footer">
              <DashboardMarkerLegend
                multipleBirthTags={getMultipleBirthLegendTags(sortedMembers)}
              />
              {showColorLegend && (
                <DashboardColorLegend lines={legendLines} />
              )}
            </div>
            </div>
        )}
      </div>
      <DashboardLineTotalsPopup
        open={showLineTotals}
        onClose={closeLineTotals}
        totals={lineTotals}
      />
      <DashboardPhotoModal photos={photoModalPhotos} onClose={closePhoto} />
      <DashboardPhotoTapHint />
    </div>
  );
}

export default TotalFamilyMembers;

